"use client";

// Libraries
import { Link, useRouter } from "@/i18n/navigation";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { useEffect, useRef, useState } from "react";
import Vimeo from "@u-wave/react-vimeo";
import { getVimeoId } from "@/helpers/vimeoId";
import { motion } from "motion/react";

// Component
import UserLayout from "@/components/ui/UserLayout/UserLayout";
import { toast } from "react-toastify";
import Logo from "@/components/ui/Logo/Logo";
import { SpinnerDotted } from "spinners-react";
import { secToHourMinS } from "@/helpers/secToMinS";
import { useTranslations } from "next-intl";

export default function WorkshopWatchComponent({
    workshop,
    locale,
}) {
    const t = useTranslations();
    const router = useRouter();
    const videoRef = useRef();

    const [isLoading, setIsLoading] = useState(false);
    const [paused, setPaused] = useState(true);
    const [showControls, setShowControls] = useState(false);
    const [video, setVideo] = useState(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [videoVolume, setVideoVolume] = useState(1);
    const [time, setTime] = useState(0);
    const [showInfos, setShowInfos] = useState(false);
    const [infosTimeout, setInfosTimeout] = useState(null);
    const [mouse, setMouse] = useState(false);
    const [subtitles, setSubtitles] = useState(null);
    const [selectedTexttrack, setSelectedTexttrack] = useState(null);
    const [subtitlesPanelOpen, setSubtitlesPanelOpen] = useState(false);

    const onStreamIsLoaded = () => {
        setPaused(true);
    };

    const onMouseIsEntered = () => {
        setShowControls(true);
    };

    const onMouseIsOut = () => {
        setShowControls(false);
    };

    const onVideoIsPlaying = (data) => {
        if (data.percent === 1) {
            // Ending workshop
        }

        setTime(data.seconds);
    };

    const updateTime = (e) => {
        setTime(e.target.value);
        video.setCurrentTime(e.target.value);
    };

    const onToggleVideo = () => {
        if (video) {
            if (paused) {
                setPaused(false);
                setShowInfos(false);
                if (infosTimeout) {
                    clearTimeout(infosTimeout);
                }
            } else {
                setPaused(true);

                let infoTimeout = setTimeout(() => {
                    setShowInfos(true);
                }, 10000);

                setInfosTimeout(infoTimeout);
            }
        }
    };

    useEffect(() => {
        const player = videoRef.current?.player;

        if (!player) return;

        const handleTimeUpdate = (data) => {
            onVideoIsPlaying(data);
        };

        player.on("timeupdate", handleTimeUpdate);

        return () => {
            player.off("timeupdate", handleTimeUpdate);
        };
    }, [video]);

    useEffect(() => {
        if (!workshop) {
            router.push("/");
        }

        const handleFullScreenChange = () => {
            if (typeof window !== "undefined") {
                setIsFullScreen(!!document.fullscreenElement);
            }
        };

        window.document.addEventListener("mouseenter", onMouseIsEntered);
        window.document.addEventListener("mousemove", onMouseIsEntered);
        window.document.addEventListener("mouseleave", onMouseIsOut);
        window.document.addEventListener(
            "fullscreenchange",
            handleFullScreenChange,
        );

        handleFullScreenChange();

        let timeout;
        window.document.addEventListener("mousemove", function () {
            setShowControls(true);
            clearTimeout(timeout);
            timeout = setTimeout(function () {
                setShowControls(false);
            }, 5500);
        });

        return () => {
            window.document.removeEventListener("mouseenter", onMouseIsEntered);
            window.document.removeEventListener("mouseleave", onMouseIsOut);
            window.document.removeEventListener(
                "fullscreenchange",
                handleFullScreenChange,
            );
        };
    }, []);

    useEffect(() => {
        if (video) {
            video.setVolume(videoVolume);
        }
    }, [videoVolume]);

    useEffect(() => {
        if (workshop) {
            setShowControls(false);
            setShowInfos(false);
        }
    }, [workshop]);

    useEffect(() => {
        if (video) {
            if (selectedTexttrack) {
                video.enableTextTrack(selectedTexttrack);
            }

            video.getTextTracks().then((tracks) => {
                setSubtitles(tracks);
            });
        }
    }, [video]);

    useEffect(() => {
        if (!showControls) {
            setMouse("cursor-none");
        } else {
            setMouse("cursor-pointer");
        }
    }, [showControls]);

    useEffect(() => {
        if (selectedTexttrack && video) {
            video.enableTextTrack(selectedTexttrack);
        }
        if (!selectedTexttrack && video) {
            video.disableTextTrack();
        }
    }, [video, selectedTexttrack]);

    return (
        <>
            {isLoading ? (
                <>
                    <div className="flex flex-col gap-8 h-screen justify-center items-center">
                        <Logo icon ratio={1.5} />
                        <SpinnerDotted
                            size={50}
                            thickness={100}
                            speed={100}
                            color="rgba(97, 59, 219, 1)"
                        />
                    </div>
                </>
            ) : (
                <>
                    <div className="bg-black h-screen relative flex flex-col justify-center">
                        {workshop && workshop.video && (
                            <>
                                {showControls && (
                                    <div
                                        className="bg-gradient-header absolute top-0 left-0 w-full text-white z-30 cursor-auto"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="flex justify-between items-center p-8">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-10 w-10 transform-all duration-150 hover:scale-110 cursor-pointer hover:text-primary transition-all"
                                                viewBox="0 0 24 24"
                                                onClick={() =>
                                                    router.push(
                                                        `/workshops`,
                                                    )
                                                }
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M20 11.25a.75.75 0 0 1 0 1.5h-9.25V18a.75.75 0 0 1-1.28.53l-6-6a.75.75 0 0 1 0-1.06l6-6a.75.75 0 0 1 1.28.53v5.25H20Z"
                                                ></path>
                                            </svg>
                                        </div>
                                    </div>
                                )}
                                <div
                                    className={`top-0 left-0 w-full h-full absolute z-10 ${mouse} flex justify-center items-center select-none`}
                                    onClick={onToggleVideo}
                                    onMouseEnter={onMouseIsEntered}
                                    onTouchMove={onMouseIsEntered}
                                    onTouchStart={onMouseIsEntered}
                                    onFocus={onMouseIsEntered}
                                >
                                    {!paused ? (
                                        <motion.div
                                            key={1}
                                            initial={{ opacity: 0 }}
                                            animate={{
                                                scale: [0, 1, 1, 1, 0],
                                                opacity: [0, 1, 1, 1, 0],
                                            }}
                                            transition={{
                                                duration: 0.5,
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-20 h-20 text-white"
                                                viewBox="0 0 24 24"
                                            >
                                                <g fill="currentColor">
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12s4.477 10 10 10Z"
                                                        clipRule="evenodd"
                                                        opacity=".5"
                                                    ></path>
                                                    <path d="m15.414 13.059l-4.72 2.787C9.934 16.294 9 15.71 9 14.786V9.214c0-.924.934-1.507 1.694-1.059l4.72 2.787c.781.462.781 1.656 0 2.118Z"></path>
                                                </g>
                                            </svg>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key={3}
                                            initial={{ opacity: 0 }}
                                            animate={{
                                                scale: [0, 1, 1, 1, 0],
                                                opacity: [0, 1, 1, 1, 0],
                                            }}
                                            transition={{
                                                duration: 0.5,
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-20 h-20 text-white"
                                                viewBox="0 0 24 24"
                                            >
                                                <g fill="currentColor">
                                                    <path
                                                        d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10Z"
                                                        opacity=".5"
                                                    ></path>
                                                    <path d="M8.076 8.617C8 8.801 8 9.034 8 9.5v5c0 .466 0 .699.076.883a1 1 0 0 0 .541.54c.184.077.417.077.883.077s.699 0 .883-.076a1 1 0 0 0 .54-.541c.077-.184.077-.417.077-.883v-5c0-.466 0-.699-.076-.883a1 1 0 0 0-.541-.54C10.199 8 9.966 8 9.5 8s-.699 0-.883.076a1 1 0 0 0-.54.541Zm5 0C13 8.801 13 9.034 13 9.5v5c0 .466 0 .699.076.883a1 1 0 0 0 .541.54c.184.077.417.077.883.077s.699 0 .883-.076a1 1 0 0 0 .54-.541c.077-.184.077-.417.077-.883v-5c0-.466 0-.699-.076-.883a1 1 0 0 0-.541-.54C15.199 8 14.966 8 14.5 8s-.699 0-.883.076a1 1 0 0 0-.54.541Z"></path>
                                                </g>
                                            </svg>
                                        </motion.div>
                                    )}
                                    {paused && showInfos && (
                                        <div className="absolute top-0 left-0 h-full w-full flex flex-col justify-center bg-black/50">
                                            <div className="2xl:w-2/3">
                                                <div
                                                    style={{
                                                        margin: "0 8%",
                                                    }}
                                                >
                                                    <div className="text-white font-light">
                                                        {t(
                                                            "workshop_watch_youAreWatching",
                                                        )}
                                                    </div>
                                                    <h1 className="text-3xl text-white mt-1">
                                                        {workshop.title}
                                                    </h1>
                                                    <div className="text-white mt-5">
                                                        {workshop.description}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {/* Controls */}
                                    {showControls && (
                                        <div
                                            className="bg-gradient-controls fixed bottom-0 left-0 w-full text-white cursor-auto"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {/* Time */}
                                            <div className="flex items-center gap-3 px-8">
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={workshop.duration}
                                                    className="transparent h-1.5 w-full cursor-pointer rounded-lg border-transparent bg-neutral-200"
                                                    value={time}
                                                    onChange={(e) =>
                                                        updateTime(e)
                                                    }
                                                    step={0.001}
                                                />
                                                {secToHourMinS(
                                                    workshop.duration - time,
                                                )}
                                            </div>
                                            <div className="flex justify-between items-center p-8">
                                                <div className="flex items-center sm:gap-10 gap-5">
                                                    {/* Play */}
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="lg:h-10 lg:w-10 h-5 w-5 transform-all duration-150 hover:scale-110 cursor-pointer hover:text-primary transition-all"
                                                        viewBox="0 0 24 24"
                                                        onClick={onToggleVideo}
                                                    >
                                                        {!paused ? (
                                                            <path
                                                                fill="currentColor"
                                                                d="M2 6c0-1.886 0-2.828.586-3.414C3.172 2 4.114 2 6 2c1.886 0 2.828 0 3.414.586C10 3.172 10 4.114 10 6v12c0 1.886 0 2.828-.586 3.414C8.828 22 7.886 22 6 22c-1.886 0-2.828 0-3.414-.586C2 20.828 2 19.886 2 18V6Zm12 0c0-1.886 0-2.828.586-3.414C15.172 2 16.114 2 18 2c1.886 0 2.828 0 3.414.586C22 3.172 22 4.114 22 6v12c0 1.886 0 2.828-.586 3.414C20.828 22 19.886 22 18 22c-1.886 0-2.828 0-3.414-.586C14 20.828 14 19.886 14 18V6Z"
                                                            ></path>
                                                        ) : (
                                                            <path
                                                                fill="currentColor"
                                                                d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.736 4 21.276 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648l12.812 6.968Z"
                                                            ></path>
                                                        )}
                                                    </svg>

                                                    {/* Sound */}
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="lg:h-10 lg:w-10 h-5 w-5 transform-all duration-150 hover:scale-110 cursor-pointer hover:text-primary transition-all"
                                                        viewBox="0 0 24 24"
                                                        onClick={() => {
                                                            if (
                                                                videoRef.current
                                                            ) {
                                                                setVideoVolume(
                                                                    videoVolume ===
                                                                        0
                                                                        ? 1
                                                                        : 0,
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        {videoVolume === 0 ? (
                                                            <g fill="currentColor">
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M20.515 6.316a.75.75 0 0 1 .991.376c.468 1.035.994 2.768.994 5.308c0 2.192-.392 3.783-.8 4.844a7.72 7.72 0 0 1-.572 1.195a4.73 4.73 0 0 1-.289.425l-.007.01l-.003.003l-.002.002L20.25 18l.576.48a.75.75 0 0 1-1.156-.956l.003-.004l.031-.041a3.27 3.27 0 0 0 .137-.212c.12-.199.288-.516.459-.961c.342-.889.7-2.298.7-4.306c0-2.326-.48-3.849-.86-4.692a.75.75 0 0 1 .375-.992Zm-2.101 2.95a.75.75 0 0 1 .887.582c.11.53.199 1.24.199 2.152c0 1.11-.132 1.923-.273 2.474a5.172 5.172 0 0 1-.203.631a2.977 2.977 0 0 1-.102.228l-.01.018l-.003.007l-.002.003v.002s-.001.001-.657-.363l.656.364a.75.75 0 0 1-1.317-.719l.005-.01a1.85 1.85 0 0 0 .038-.087a3.66 3.66 0 0 0 .141-.447c.11-.424.227-1.111.227-2.101a9.17 9.17 0 0 0-.168-1.848a.75.75 0 0 1 .582-.886Z"
                                                                    clipRule="evenodd"
                                                                ></path>
                                                                <path d="M21.78 3.53a.75.75 0 0 0-1.06-1.06l-4.45 4.449a11.265 11.265 0 0 0-.193-1.39c-.172-.788-.477-1.473-1.116-1.923a3.066 3.066 0 0 0-.769-.39c-.818-.28-1.631-.057-2.457.345c-.814.395-1.8 1.046-3.032 1.857l-.267.176c-.447.295-.602.394-.76.464a2.17 2.17 0 0 1-.535.16c-.171.03-.354.032-.89.032h-.162c-1.217 0-2.062-.001-2.814.347A3.962 3.962 0 0 0 1.548 8.22c-.392.729-.438 1.491-.504 2.575l-.008.13C1.014 11.294 1 11.658 1 12c0 .342.014.706.036 1.074l.008.13c.066 1.084.112 1.846.504 2.575a3.962 3.962 0 0 0 1.727 1.624c.61.283 1.283.336 2.166.345L2.72 20.47a.75.75 0 1 0 1.06 1.06l18-18ZM16.5 12a.75.75 0 0 0-1.255-.554l-.071.074l-6 6.274A.778.778 0 0 0 9.34 19c1.039.68 1.899 1.225 2.631 1.549c.743.328 1.48.489 2.222.236a3.08 3.08 0 0 0 .769-.391c.706-.497 1.005-1.28 1.167-2.18c.159-.884.213-2.056.281-3.516l.003-.058a68.23 68.23 0 0 0 .088-2.64Z"></path>
                                                            </g>
                                                        ) : (
                                                            <>
                                                                {videoVolume >=
                                                                0.5 ? (
                                                                    <g fill="currentColor">
                                                                        <path d="M2.003 11.716c.037-1.843.056-2.764.668-3.552a3.18 3.18 0 0 1 .413-.431c.752-.636 1.746-.636 3.733-.636c.71 0 1.065 0 1.403-.092c.07-.02.14-.042.209-.067c.33-.121.627-.33 1.22-.746c2.338-1.645 3.508-2.467 4.489-2.11c.188.069.37.168.533.29c.848.635.913 2.115 1.042 5.073a70.58 70.58 0 0 1 .08 2.555c0 .521-.032 1.46-.08 2.555c-.13 2.958-.194 4.438-1.042 5.073a2.109 2.109 0 0 1-.533.29c-.982.357-2.15-.465-4.49-2.11c-.592-.416-.889-.625-1.22-.746a2.61 2.61 0 0 0-.208-.067c-.338-.092-.693-.092-1.403-.092c-1.987 0-2.98 0-3.733-.636a3.145 3.145 0 0 1-.413-.43c-.612-.79-.63-1.71-.668-3.553a14.06 14.06 0 0 1 0-.569Z"></path>
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M19.49 5.552a.66.66 0 0 1 .97.094l-.529.471l.53-.47l.002.002l.003.004l.007.009a2.572 2.572 0 0 1 .079.112c.047.071.111.173.186.305c.149.264.339.652.526 1.171C21.64 8.291 22 9.851 22 12c0 2.15-.36 3.71-.736 4.75c-.187.52-.377.907-.526 1.172a4.655 4.655 0 0 1-.265.417l-.007.009l-.003.003l-.001.002s-.001.001-.531-.47l.53.471a.66.66 0 0 1-.971.094a.77.77 0 0 1-.09-1.035l.03-.041c.026-.04.07-.11.125-.207a6.28 6.28 0 0 0 .422-.943c.314-.871.644-2.253.644-4.222c0-1.97-.33-3.35-.644-4.222a6.28 6.28 0 0 0-.422-.942a3.152 3.152 0 0 0-.157-.253m-1.641 1.833c.333-.197.753-.07.938.286l-.603.357l.603-.357l.001.002l.002.003l.003.007l.01.018l.024.053c.018.042.042.099.07.17c.053.145.12.35.185.62c.13.54.252 1.337.252 2.425c0 1.089-.122 1.886-.252 2.426c-.065.27-.132.475-.186.619a3.04 3.04 0 0 1-.094.223l-.009.018l-.003.007l-.002.003v.002s-.001.001-.604-.356l.603.357c-.185.355-.605.483-.938.286c-.33-.196-.45-.638-.272-.991l.004-.01l.035-.085c.032-.086.08-.23.13-.438c.1-.416.208-1.09.208-2.06c0-.971-.108-1.645-.208-2.06a3.757 3.757 0 0 0-.165-.524l-.004-.01a.76.76 0 0 1 .272-.991Z"
                                                                            clipRule="evenodd"
                                                                        ></path>
                                                                    </g>
                                                                ) : (
                                                                    <g fill="currentColor">
                                                                        <path d="M3.003 11.716c.04-1.843.059-2.764.697-3.552c.117-.144.288-.315.432-.431c.785-.636 1.822-.636 3.897-.636c.741 0 1.112 0 1.465-.092c.073-.02.146-.042.218-.067c.345-.121.654-.33 1.273-.746c2.442-1.645 3.662-2.467 4.687-2.11c.196.069.387.168.556.29c.886.635.953 2.115 1.088 5.073c.05 1.096.084 2.034.084 2.555c0 .521-.034 1.46-.084 2.555c-.134 2.958-.202 4.438-1.088 5.073c-.17.122-.36.221-.556.29c-1.025.357-2.245-.465-4.687-2.11c-.619-.416-.928-.625-1.273-.746a2.815 2.815 0 0 0-.218-.067c-.353-.092-.724-.092-1.465-.092c-2.075 0-3.112 0-3.897-.636a3.21 3.21 0 0 1-.432-.43c-.638-.79-.658-1.71-.697-3.553a13.443 13.443 0 0 1 0-.569Z"></path>
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M19.45 8.416a.712.712 0 0 1 .98.286l-.63.357l.63-.357v.002l.002.003l.004.007l.01.018a1.975 1.975 0 0 1 .098.224c.056.144.126.349.193.619c.136.54.263 1.337.263 2.425c0 1.089-.127 1.886-.263 2.426c-.067.27-.137.475-.193.619a2.921 2.921 0 0 1-.099.223l-.009.018l-.004.007l-.001.003v.002s-.002.001-.631-.356l.63.357a.712.712 0 0 1-.98.286a.744.744 0 0 1-.284-.991l.005-.01c.006-.014.019-.043.035-.085a3.69 3.69 0 0 0 .137-.438c.104-.416.217-1.09.217-2.06c0-.971-.113-1.645-.217-2.06a3.657 3.657 0 0 0-.172-.524l-.005-.01a.744.744 0 0 1 .284-.991Z"
                                                                            clipRule="evenodd"
                                                                        ></path>
                                                                    </g>
                                                                )}
                                                            </>
                                                        )}
                                                    </svg>
                                                    <div className="sm:w-full w-10">
                                                        <input
                                                            type="range"
                                                            min={0}
                                                            max={100}
                                                            className="transparent h-1.5 w-full cursor-pointer rounded-lg border-transparent bg-neutral-200"
                                                            value={
                                                                videoVolume *
                                                                100
                                                            }
                                                            onChange={(e) => {
                                                                setVideoVolume(
                                                                    e.target
                                                                        .value /
                                                                        100,
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="lg:flex hidden items-center gap-5">
                                                    <b>{workshop.title}</b>
                                                </div>
                                                {/* Full screen and subtitles */}
                                                <div className="flex items-center lg:gap-10 gap-8 lg:relative">
                                                    {subtitlesPanelOpen && (
                                                        <div className="absolute bg-bgDark-900/95 backdrop-blur-lg lg:bottom-16 rounded-2xl bottom-24 lg:right-20 right-0 max-h-96 overflow-y-auto lg:w-56 w-full cursor-auto z-40 border border-borderDark shadow-2xl">
                                                            <div className="p-3">
                                                                <div className="text-xs font-semibold text-gray-400 px-3 py-2 uppercase tracking-wider">
                                                                    Sous-titres
                                                                </div>
                                                                <div
                                                                    className={`px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 flex items-center justify-between group ${
                                                                        selectedTexttrack === null
                                                                            ? "bg-primary text-white"
                                                                            : "hover:bg-bgDark-700 text-gray-300"
                                                                    }`}
                                                                    onClick={() =>
                                                                        setSelectedTexttrack(
                                                                            null,
                                                                        )
                                                                    }
                                                                >
                                                                    <span className="text-sm font-medium">
                                                                        {t(
                                                                            "workshop_watch_noSubtitles",
                                                                        )}
                                                                    </span>
                                                                    {selectedTexttrack === null && (
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-5 w-5"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            stroke="currentColor"
                                                                            strokeWidth="2.5"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        >
                                                                            <polyline points="20 6 9 17 4 12"></polyline>
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                                <div className="h-px bg-borderDark my-2"></div>
                                                                {subtitles.map(
                                                                    (subtitle) => (
                                                                        <div
                                                                            key={
                                                                                subtitle.label
                                                                            }
                                                                            className={`px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 flex items-center justify-between group ${
                                                                                selectedTexttrack ===
                                                                                subtitle.language
                                                                                    ? "bg-primary text-white"
                                                                                    : "hover:bg-bgDark-700 text-gray-300"
                                                                            }`}
                                                                            onClick={() =>
                                                                                setSelectedTexttrack(
                                                                                    subtitle.language,
                                                                                )
                                                                            }
                                                                        >
                                                                            <span className="text-sm font-medium">
                                                                                {
                                                                                    subtitle.label
                                                                                }
                                                                            </span>
                                                                            {selectedTexttrack ===
                                                                                subtitle.language && (
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    className="h-5 w-5"
                                                                                    viewBox="0 0 24 24"
                                                                                    fill="none"
                                                                                    stroke="currentColor"
                                                                                    strokeWidth="2.5"
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                >
                                                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                                                </svg>
                                                                            )}
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {subtitles && (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="lg:h-10 lg:w-10 h-5 w-5 transform-all duration-150 hover:scale-110 cursor-pointer hover:text-primary transition-all"
                                                            viewBox="0 0 24 24"
                                                            onClick={() => {
                                                                setSubtitlesPanelOpen(
                                                                    !subtitlesPanelOpen,
                                                                );
                                                            }}
                                                        >
                                                            <g
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="1.5"
                                                            >
                                                                <path d="M2 12c0-3.771 0-5.657 1.172-6.828C4.343 4 6.229 4 10 4h4c3.771 0 5.657 0 6.828 1.172C22 6.343 22 8.229 22 12c0 3.771 0 5.657-1.172 6.828C19.657 20 17.771 20 14 20h-4c-3.771 0-5.657 0-6.828-1.172C2 17.657 2 15.771 2 12Z"></path>
                                                                <path
                                                                    strokeLinecap="round"
                                                                    d="M10 16H6m8-3h4m-4 3h-1.5m-3-3h2m6.5 3h-1.5M6 13h1"
                                                                ></path>
                                                            </g>
                                                        </svg>
                                                    )}
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="lg:w-10 lg:h-10 w-5 h-5 transform-all duration-150 hover:scale-110 cursor-pointer hover:text-primary transition-all"
                                                        viewBox="0 0 24 24"
                                                        onClick={() => {
                                                            if (
                                                                window.document
                                                                    .fullscreenElement
                                                            ) {
                                                                setIsFullScreen(
                                                                    false,
                                                                );
                                                                window.document.exitFullscreen();
                                                            } else {
                                                                setIsFullScreen(
                                                                    true,
                                                                );
                                                                window.document.documentElement.requestFullscreen();
                                                            }
                                                        }}
                                                    >
                                                        {isFullScreen ? (
                                                            <path
                                                                fill="currentColor"
                                                                fillRule="evenodd"
                                                                d="M14 1.25a.75.75 0 0 1 .75.75c0 1.907.002 3.261.14 4.29c.135 1.005.389 1.585.812 2.008c.423.423 1.003.677 2.009.812c1.027.138 2.382.14 4.289.14a.75.75 0 0 1 0 1.5h-.056c-1.838 0-3.294 0-4.433-.153c-1.172-.158-2.121-.49-2.87-1.238c-.748-.749-1.08-1.698-1.238-2.87c-.153-1.14-.153-2.595-.153-4.433V2a.75.75 0 0 1 .75-.75Zm-4 0a.75.75 0 0 1 .75.75v.056c0 1.838 0 3.294-.153 4.433c-.158 1.172-.49 2.121-1.238 2.87c-.749.748-1.698 1.08-2.87 1.238c-1.14.153-2.595.153-4.433.153H2a.75.75 0 0 1 0-1.5c1.907 0 3.261-.002 4.29-.14c1.005-.135 1.585-.389 2.008-.812c.423-.423.677-1.003.812-2.009c.138-1.028.14-2.382.14-4.289a.75.75 0 0 1 .75-.75ZM1.25 14a.75.75 0 0 1 .75-.75h.056c1.838 0 3.294 0 4.433.153c1.172.158 2.121.49 2.87 1.238c.748.749 1.08 1.698 1.238 2.87c.153 1.14.153 2.595.153 4.433V22a.75.75 0 0 1-1.5 0c0-1.907-.002-3.262-.14-4.29c-.135-1.005-.389-1.585-.812-2.008c-.423-.423-1.003-.677-2.009-.812c-1.028-.138-2.382-.14-4.289-.14a.75.75 0 0 1-.75-.75Zm20.694-.75H22a.75.75 0 0 1 0 1.5c-1.907 0-3.262.002-4.29.14c-1.005.135-1.585.389-2.008.812c-.423.423-.677 1.003-.812 2.009c-.138 1.027-.14 2.382-.14 4.289a.75.75 0 0 1-1.5 0v-.056c0-1.838 0-3.294.153-4.433c.158-1.172.49-2.121 1.238-2.87c.749-.748 1.698-1.08 2.87-1.238c1.14-.153 2.595-.153 4.433-.153Z"
                                                                clipRule="evenodd"
                                                            ></path>
                                                        ) : (
                                                            <path
                                                                className=""
                                                                fill="currentColor"
                                                                fillRule="evenodd"
                                                                d="M9.944 1.25H10a.75.75 0 0 1 0 1.5c-1.907 0-3.261.002-4.29.14c-1.005.135-1.585.389-2.008.812c-.423.423-.677 1.003-.812 2.009c-.138 1.028-.14 2.382-.14 4.289a.75.75 0 0 1-1.5 0v-.056c0-1.838 0-3.294.153-4.433c.158-1.172.49-2.121 1.238-2.87c.749-.748 1.698-1.08 2.87-1.238c1.14-.153 2.595-.153 4.433-.153Zm8.345 1.64c-1.027-.138-2.382-.14-4.289-.14a.75.75 0 0 1 0-1.5h.056c1.838 0 3.294 0 4.433.153c1.172.158 2.121.49 2.87 1.238c.748.749 1.08 1.698 1.238 2.87c.153 1.14.153 2.595.153 4.433V10a.75.75 0 0 1-1.5 0c0-1.907-.002-3.261-.14-4.29c-.135-1.005-.389-1.585-.812-2.008c-.423-.423-1.003-.677-2.009-.812ZM2 13.25a.75.75 0 0 1 .75.75c0 1.907.002 3.262.14 4.29c.135 1.005.389 1.585.812 2.008c.423.423 1.003.677 2.009.812c1.028.138 2.382.14 4.289.14a.75.75 0 0 1 0 1.5h-.056c-1.838 0-3.294 0-4.433-.153c-1.172-.158-2.121-.49-2.87-1.238c-.748-.749-1.08-1.698-1.238-2.87c-.153-1.14-.153-2.595-.153-4.433V14a.75.75 0 0 1 .75-.75Zm20 0a.75.75 0 0 1 .75.75v.056c0 1.838 0 3.294-.153 4.433c-.158 1.172-.49 2.121-1.238 2.87c-.749.748-1.698 1.08-2.87 1.238c-1.14.153-2.595.153-4.433.153H14a.75.75 0 0 1 0-1.5c1.907 0 3.262-.002 4.29-.14c1.005-.135 1.585-.389 2.008-.812c.423-.423.677-1.003.812-2.009c.138-1.027.14-2.382.14-4.289a.75.75 0 0 1 .75-.75Z"
                                                                clipRule="evenodd"
                                                            ></path>
                                                        )}
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Vimeo
                                        video={getVimeoId(workshop.video)}
                                        showByline={false}
                                        color="8468e3"
                                        showTitle={false}
                                        showPortrait={false}
                                        background={false}
                                        speed
                                        className="object-contain"
                                        dnt
                                        loop={false}
                                        responsive
                                        ref={videoRef}
                                        paused={paused}
                                        onLoaded={onStreamIsLoaded}
                                        volume={videoVolume}
                                        onPlaying={() => setPaused(false)}
                                        onTimeUpdate={(data) =>
                                            onVideoIsPlaying(data)
                                        }
                                        controls={false}
                                        autoplay={typeof window !== "undefined" && window.innerWidth > 768}
                                        onReady={setVideo}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </>
    );
}
